import React, { useEffect, useState } from 'react';
import { expoamsClient } from './services/index.js';
import { InvalidSheetsError, type SheetValidationResult } from './services/errors.js';

export interface SheetSettingsProps {
  sheetIds: string[];
  sheetNames: Record<string, string>;
  onSheetsChanged: () => void | Promise<void>;
}

interface SheetRow {
  id: string;
  status: 'valid' | 'pending' | 'validating' | 'invalid';
  displayName?: string;
  errorReason?: string;
  key: string;
}

function freshKey(): string {
  return `row-${Math.random().toString(36).slice(2, 10)}`;
}

function rowsFromProps(ids: string[], names: Record<string, string>): SheetRow[] {
  return ids.map((id) => {
    const row: SheetRow = { id, status: 'valid', key: freshKey() };
    const name = names[id];
    if (name !== undefined) row.displayName = name;
    return row;
  });
}

export const SheetSettings: React.FC<SheetSettingsProps> = ({
  sheetIds,
  sheetNames,
  onSheetsChanged,
}) => {
  const [rows, setRows] = useState<SheetRow[]>(() => rowsFromProps(sheetIds, sheetNames));
  const [submitting, setSubmitting] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);

  useEffect(() => {
    setRows(rowsFromProps(sheetIds, sheetNames));
  }, [sheetIds, sheetNames]);

  function patch(key: string, update: Partial<SheetRow>): void {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...update } : r)));
  }

  function addRow(): void {
    setRows((rs) => [...rs, { id: '', status: 'pending', key: freshKey() }]);
  }

  function removeRow(key: string): void {
    setRows((rs) => rs.filter((r) => r.key !== key));
  }

  async function validateRow(row: SheetRow): Promise<void> {
    if (!row.id.trim()) return;
    // Clear optional fields by omitting them from the spread
    setRows((rs) =>
      rs.map((r) => {
        if (r.key !== row.key) return r;
        const updated: SheetRow = { id: r.id, status: 'validating', key: r.key };
        return updated;
      }),
    );
    const result = await expoamsClient.validateSheet({ sheetId: row.id });
    if (result.valid) {
      const update: Partial<SheetRow> = { status: 'valid' };
      if (result.name !== undefined) update.displayName = result.name;
      patch(row.key, update);
    } else {
      const errorMsg = result.error ?? 'invalid';
      patch(row.key, { status: 'invalid', errorReason: errorMsg });
    }
  }

  function canSave(): boolean {
    if (submitting) return false;
    if (rows.length === 0) return false;
    const ids = rows.map((r) => r.id.trim());
    if (ids.some((id) => id.length === 0)) return false;
    if (new Set(ids).size !== ids.length) return false;
    return true;
  }

  async function saveAll(): Promise<void> {
    setSubmitting(true);
    setBanner(null);
    try {
      const result = await expoamsClient.updateSheetConfig({
        sheetIds: rows.map((r) => r.id.trim()),
      });
      setRows(
        result.sheetIds.map((id) => {
          const row: SheetRow = { id, status: 'valid', key: freshKey() };
          const resolvedName = result.resolvedNames[id];
          if (resolvedName !== undefined) row.displayName = resolvedName;
          return row;
        }),
      );
      const when = new Date().toLocaleString('en-US', {
        timeZone: 'America/Los_Angeles',
        hour12: false,
      });
      setBanner(
        `Saved — ${result.sheetIds.length} sheets configured by ${result.updatedBy} at ${when} PT`,
      );
      await onSheetsChanged();
    } catch (err) {
      if (err instanceof InvalidSheetsError) {
        applyInvalidSheetsResults(err.results);
      } else {
        setBanner(err instanceof Error ? `Error: ${err.message}` : 'Error: unknown');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function applyInvalidSheetsResults(results: SheetValidationResult[]): void {
    setRows((rs) =>
      rs.map((r) => {
        const match = results.find((res) => res.sheetId === r.id.trim());
        if (!match) return r;
        if (match.ok) {
          const updated: SheetRow = { id: r.id, status: 'valid', key: r.key };
          if (match.displayName !== undefined) updated.displayName = match.displayName;
          return updated;
        } else {
          const updated: SheetRow = { id: r.id, status: 'invalid', key: r.key };
          updated.errorReason = match.reason ?? 'invalid';
          return updated;
        }
      }),
    );
  }

  return (
    <div className="expoams-sheet-settings">
      <h3 style={{ margin: '0 0 1rem' }}>Registered Inventory Sheets</h3>

      {banner && (
        <div
          className="expoams-settings-banner"
          style={{
            marginBottom: '1rem',
            padding: '0.75rem',
            background: banner.startsWith('Saved') ? '#f0fdf4' : '#fef2f2',
            borderRadius: '8px',
            fontSize: '0.85rem',
          }}
        >
          {banner}
        </div>
      )}

      {rows.length === 0 && (
        <div className="expoams-settings-empty" style={{ color: '#999', marginBottom: '1rem' }}>
          No sheets configured. Use &ldquo;+ Add sheet&rdquo; to register one.
        </div>
      )}

      {rows.map((r) => (
        <div
          key={r.key}
          className={`expoams-settings-row expoams-settings-row-${r.status}`}
          style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}
        >
          <input
            type="text"
            value={r.id}
            placeholder="paste new sheet ID"
            onChange={(e) => {
              const update: Partial<SheetRow> = { id: e.target.value, status: 'pending' };
              patch(r.key, update);
              // Clear displayName and errorReason by rebuilding the row cleanly
              setRows((rs) =>
                rs.map((row) => {
                  if (row.key !== r.key) return row;
                  return { id: e.target.value, status: 'pending', key: row.key };
                }),
              );
            }}
            style={{ flex: 2, fontFamily: 'monospace', fontSize: '12px' }}
          />
          <span style={{ flex: 1, fontSize: '12px' }}>
            {r.status === 'invalid' ? (
              <span style={{ color: '#991b1b' }}>{r.errorReason}</span>
            ) : (
              <span>{r.displayName ?? (r.status === 'pending' ? '— not checked —' : '—')}</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => void validateRow(r)}
            disabled={r.status === 'validating'}
          >
            {r.status === 'validating' ? 'Validating…' : 'Validate'}
          </button>
          <button
            type="button"
            onClick={() => removeRow(r.key)}
            aria-label={`Remove sheet ${r.id}`}
          >
            ✕
          </button>
        </div>
      ))}

      <button type="button" onClick={addRow} style={{ marginBottom: '1rem' }}>
        + Add sheet
      </button>

      <div style={{ borderTop: '1px solid #eee', paddingTop: '12px' }}>
        <button type="button" onClick={() => void saveAll()} disabled={!canSave()}>
          {submitting ? 'Saving…' : `Save all (${rows.length} row${rows.length === 1 ? '' : 's'})`}
        </button>
      </div>
    </div>
  );
};

export default SheetSettings;
